/* Feb-2026 · "Generate Synopsis" — reads whatever chapters exist (a
 * partial manuscript is fine) and shows the full synopsis Claude
 * infers from them, structured as either a 3-act (classic) or 5-act
 * (Freytag pyramid) story — pick the structure before generating.
 * Unwritten acts/beats are clearly marked "Inferred" rather than
 * passed off as literal manuscript content. Copy-to-clipboard, or
 * push it into a Framework doc (new or the project's already-linked
 * one) so it can be fleshed out further and reused as a template for
 * future stories.
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ScrollText, Copy, BookMarked, RefreshCw } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { novelBuildApi, frameworkApi } from '@/lib/api';

// Mirrors the structural hints baked into the backend prompt
// (manuscript_analyzer.py's `_synopsis_schema`) — purely cosmetic
// subtitles here, the LLM output itself doesn't repeat the label.
const ACT_LABELS = {
  '3-act': { 1: 'Setup', 2: 'Confrontation', 3: 'Resolution' },
  '5-act': { 1: 'Exposition', 2: 'Rising Action', 3: 'Climax', 4: 'Falling Action', 5: 'Denouement' },
};
const ACT_COUNT = { '3-act': 3, '5-act': 5 };

function ActCard({ number, label, text, inferred }) {
  if (!text) return null;
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-3" data-testid={`synopsis-act-${number}`}>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
          Act {number}{label ? ` · ${label}` : ''}
        </span>
        {inferred && (
          <span
            className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-amber-600"
            data-testid={`synopsis-inferred-badge-act-${number}`}
          >
            Inferred
          </span>
        )}
      </div>
      <p className="text-[14px] leading-relaxed text-foreground/90">{text}</p>
    </div>
  );
}

function buildPlainText(analysis, actStructure) {
  if (!analysis) return '';
  const s = analysis.three_act_synopsis || {};
  const labels = ACT_LABELS[actStructure] || ACT_LABELS['3-act'];
  const total = ACT_COUNT[actStructure] || 3;
  const lines = [];
  for (let i = 1; i <= total; i += 1) {
    const text = s[`act_${i}`];
    if (!text) continue;
    const isLast = i === total;
    const tag = isLast && !analysis.is_complete ? ' (inferred — not yet written)' : '';
    lines.push(`ACT ${i} · ${labels[i] || ''}${tag}\n${text}`);
  }
  const remaining = analysis.remaining_chapters_outline || [];
  if (remaining.length) {
    lines.push(
      'PLANNED REMAINING CHAPTERS (inferred)\n'
      + remaining.map((r) => `${r.number ? `Ch. ${r.number}: ` : ''}${r.beat || r.title || ''}`).join('\n'),
    );
  }
  return lines.join('\n\n');
}

export default function SynopsisDialog({ open, onClose, project }) {
  const navigate = useNavigate();
  const [actStructure, setActStructure] = useState('3-act');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [saving, setSaving] = useState(null); // 'existing' | 'new' | null

  const hasLinkedFramework = Boolean(project?.from_framework_id);

  const run = useCallback(async (structure) => {
    if (!project?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await novelBuildApi.analyze(project.id, { act_structure: structure || actStructure });
      setAnalysis(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not generate a synopsis — please try again.');
    } finally {
      setLoading(false);
    }
  }, [project?.id, actStructure]);

  React.useEffect(() => {
    if (open && !analysis && !loading && !error) run();
  }, [open, analysis, loading, error, run]);

  // Reset if the underlying project changes under this mounted dialog
  // (e.g. the writer switches projects without a full page remount).
  React.useEffect(() => {
    setAnalysis(null);
    setError(null);
  }, [project?.id]);

  const handleStructureChange = (structure) => {
    if (structure === actStructure) return;
    setActStructure(structure);
    setAnalysis(null);
    setError(null);
    run(structure);
  };

  const handleClose = () => {
    onClose?.();
  };

  const handleCopy = async () => {
    const text = buildPlainText(analysis, actStructure);
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Synopsis copied to clipboard.');
    } catch {
      toast.error('Could not copy — your browser blocked clipboard access.');
    }
  };

  const handleSave = async (mode) => {
    if (!analysis) return;
    setSaving(mode);
    try {
      const res = await frameworkApi.saveSynopsis({
        project_id: project.id,
        synopsis_text: buildPlainText(analysis, actStructure),
        framework_id: mode === 'existing' ? project.from_framework_id : undefined,
      });
      toast.success(
        res.created ? 'Saved as a new framework.' : 'Added to the linked framework.',
        {
          action: {
            label: 'Open framework',
            onClick: () => navigate(`/framework/${res.framework_id}`),
          },
        },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save to a framework.');
    } finally {
      setSaving(null);
    }
  };

  const s = analysis?.three_act_synopsis || {};
  const remaining = analysis?.remaining_chapters_outline || [];
  const cast = (analysis?.characters || []).slice(0, 6);
  const actTotal = ACT_COUNT[actStructure] || 3;
  const labels = ACT_LABELS[actStructure] || ACT_LABELS['3-act'];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="flex max-h-[90vh] max-w-xl flex-col overflow-hidden" data-testid="synopsis-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ScrollText className="h-4 w-4 text-primary" /> Generate Synopsis
          </DialogTitle>
          <DialogDescription>
            From the chapters you've drafted so far — unwritten parts are inferred, not invented as fact.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-shrink-0 flex items-center gap-1 rounded-md bg-muted/40 p-0.5" data-testid="synopsis-structure-toggle" role="group" aria-label="Act structure">
          {['3-act', '5-act'].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleStructureChange(opt)}
              disabled={loading}
              data-testid={`synopsis-structure-${opt}`}
              className={`flex-1 rounded-sm px-3 py-1.5 text-[13.5px] font-semibold transition disabled:opacity-60 ${
                actStructure === opt ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {opt === '3-act' ? '3-Act (Setup · Confrontation · Resolution)' : '5-Act (Freytag Pyramid)'}
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-muted-foreground" data-testid="synopsis-loading">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-[14px]">Reading your manuscript and drafting a synopsis…</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="synopsis-error">
            <p className="text-[14px] text-destructive">{error}</p>
            <Button size="sm" variant="outline" onClick={() => run()} data-testid="synopsis-retry-btn">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Try again
            </Button>
          </div>
        )}

        {!loading && !error && analysis && (
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1 scroll-thin">
            <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2" data-testid="synopsis-progress-line">
              <span className="text-[13.5px] text-muted-foreground">
                {analysis.drafted_chapters} of {analysis.target_chapters} chapters drafted
              </span>
              {analysis.is_complete ? (
                <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[13px] font-semibold text-emerald-600">
                  Manuscript complete
                </span>
              ) : (
                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[13px] font-semibold text-amber-600">
                  In progress
                </span>
              )}
            </div>

            {Array.from({ length: actTotal }, (_, idx) => idx + 1).map((n) => (
              <ActCard
                key={n}
                number={n}
                label={labels[n]}
                text={s[`act_${n}`]}
                inferred={n === actTotal && !analysis.is_complete}
              />
            ))}

            {!analysis.is_complete && remaining.length > 0 && (
              <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-3" data-testid="synopsis-remaining-outline">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Planned remaining chapters
                  </span>
                  <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-amber-600">
                    Inferred
                  </span>
                </div>
                <ul className="space-y-1 text-[13.5px] text-foreground/80">
                  {remaining.slice(0, 12).map((r, i) => (
                    <li key={i}>
                      {r.number ? <span className="font-mono-stat text-muted-foreground">Ch. {r.number} · </span> : null}
                      {r.beat || r.title || ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {cast.length > 0 && (
              <p className="text-[13.5px] text-muted-foreground" data-testid="synopsis-cast-line">
                <span className="font-semibold text-foreground/80">Cast so far: </span>
                {cast.map((c) => c.name).filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        )}

        {!loading && !error && analysis && (
          <div className="flex-shrink-0 flex flex-wrap items-center gap-2 border-t border-border pt-3">
            <Button size="sm" variant="outline" onClick={handleCopy} data-testid="synopsis-copy-btn">
              <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy synopsis
            </Button>
            {hasLinkedFramework ? (
              <>
                <Button size="sm" onClick={() => handleSave('existing')} disabled={Boolean(saving)} data-testid="synopsis-save-existing-btn">
                  {saving === 'existing' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <BookMarked className="mr-1.5 h-3.5 w-3.5" />}
                  Save to linked framework
                </Button>
                <button
                  type="button"
                  onClick={() => handleSave('new')}
                  disabled={Boolean(saving)}
                  className="text-[13.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
                  data-testid="synopsis-save-new-btn"
                >
                  {saving === 'new' ? 'Saving…' : 'or save as a new framework instead'}
                </button>
              </>
            ) : (
              <Button size="sm" onClick={() => handleSave('new')} disabled={Boolean(saving)} data-testid="synopsis-save-new-btn">
                {saving === 'new' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <BookMarked className="mr-1.5 h-3.5 w-3.5" />}
                Save as new framework
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => run()} disabled={loading} className="ml-auto" data-testid="synopsis-regenerate-btn">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Regenerate
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
